import { controlToastState } from "risa-oasis-ui_v2";
import { AestraPrimaryCareTable } from "../../data-model/aestraPrimaryCareTable";
import { API_ENDPOINTS } from "../../enums/apiUrls";
import { logDataToConsole, logError } from "../../utils/customLogger";
import { getBearerToken } from "../postCall/bearerToken";

const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const token = await getBearerToken();
  if (!token) {
    controlToastState("bearerToken-not-available");
  }
  return {
    Authorization: `Bearer ${token}`,
  };
};

const createRequestOptions = (
  body?: Record<string, unknown>,
  header?: Record<string, string>,
  requestType: "POST" | "GET" = "POST",
  signal?: AbortSignal,
): RequestInit => {
  const headers = new Headers();
  headers.append("Content-Type", "application/json");
  if (header) {
    for (const key in header) {
      headers.append(key, header[key]);
    }
  }

  const options: RequestInit = {
    method: requestType,
    headers,
    redirect: "follow",
    ...(signal && { signal }),
  };

  if (requestType === "POST" && body) {
    options.body = JSON.stringify(body);
  }

  return options;
};

const handleApiResponse = async (response: Response): Promise<any> => {
  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }
  return await response.json();
};

const fetchApiData = async (
  url: string,
  logKey: string,
  body?: Record<string, unknown>,
  headers?: Record<string, string>,
  requestType: "POST" | "GET" = "POST",
  signal?: AbortSignal,
): Promise<any> => {
  const requestOptions = createRequestOptions(
    body,
    headers ?? {},
    requestType,
    signal,
  );

  try {
    const response = await fetch(url, requestOptions);
    const result = await handleApiResponse(response);
    logDataToConsole(logKey, result);
    return result;
  } catch (error) {
    logError(error as Error, `Error in ${logKey}`);
    throw error;
  }
};

export interface FetchPrimaryCareDataParams {
  date_from_start?: string;
  date_from_end?: string;
  filters?: {
    patient_mrn?: string[];
    [key: string]: any;
  };
  page?: number;
  page_size?: number;
  request_id?: string;
  org_id?: string;
  keyword?: string;
  cmm_fields?: string[];
}

export interface PrimaryCareApiResponse {
  success: boolean;
  message: string;
  details: {
    rows: PrimaryCareApiRow[];
    pagination: {
      total_count: number;
      page: number;
      page_size: number;
      total_pages: number;
    };
  };
}

/** API row shape for primary care orders (same fields as table state). */
export type PrimaryCareApiRow = AestraPrimaryCareTable;

export const fetchPrimaryCareData = async (
  params: FetchPrimaryCareDataParams,
  signal?: AbortSignal,
): Promise<PrimaryCareApiResponse> => {
  const body: Record<string, unknown> = {
    org_id: params.org_id ?? "",
    page: params.page ?? 1,
    limit: params.page_size ?? 100,
    cmm_fields: params.cmm_fields ?? [
      "cmm_status",
      "error_text",
      "cmm_result_key",
    ],
  };

  if (params.filters && Object.keys(params.filters).length > 0) {
    body.filters = params.filters;
  }

  const headers = await getAuthHeaders();

  return await fetchApiData(
    API_ENDPOINTS.GET_PRIMARY_CARE_ORDERS,
    "fetchPrimaryCareData",
    body,
    headers,
    "POST",
    signal,
  );
};

export const searchPrimaryCareData = async (
  params: FetchPrimaryCareDataParams,
  signal?: AbortSignal,
): Promise<PrimaryCareApiResponse> => {
  const body: Record<string, unknown> = {
    org_id: params.org_id ?? "",
    keyword: params.keyword ?? "",
    page: params.page ?? 1,
    limit: params.page_size ?? 100,
    cmm_fields: params.cmm_fields ?? [
      "cmm_status",
      "error_text",
      "cmm_result_key",
    ],
  };

  const headers = await getAuthHeaders();

  return await fetchApiData(
    API_ENDPOINTS.SEARCH_PRIMARY_CARE,
    "searchPrimaryCareData",
    body,
    headers,
    "POST",
    signal,
  );
};
